import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

// Register the web component
import '../../../widget/index';

export interface WebchatWidgetProps {
  serverUrl?: string;
  widgetId?: string | null;
  auth?: string;
  displayMode?: 'floating' | 'fullscreen';
  primaryColor?: string;
  position?: string;
  language?: string;
  title?: string;
  greeting?: string;
  otpGreeting?: string;
  sessionTimeout?: number;
  preview?: boolean;
  portalMode?: boolean;
  style?: React.CSSProperties;
  onEvent?: (type: string, detail: unknown) => void;
}

/** All widget events the wrapper listens for. */
const WIDGET_EVENTS = [
  'mcp-chat:open',
  'mcp-chat:close',
  'mcp-chat:message-sent',
  'mcp-chat:message-received',
  'mcp-chat:turn-timeout',
  'mcp-chat:turn-retry',
  'mcp-chat:turn-dismiss',
  'mcp-chat:authenticated',
  'mcp-chat:unauthenticated',
  'mcp-chat:error',
  'mcp-chat:expand',
  'mcp-chat:collapse',
];

/**
 * Attribute mapping: prop name → HTML attribute name.
 * Order matters - layout/identity attributes first, then cosmetic ones.
 */
const ATTR_MAP: [keyof WebchatWidgetProps, string][] = [
  ['displayMode', 'display-mode'],
  ['widgetId', 'widget-id'],
  ['serverUrl', 'server-url'],
  ['auth', 'auth'],
  ['language', 'language'],
  ['primaryColor', 'primary-color'],
  ['position', 'position'],
  ['sessionTimeout', 'session-timeout'],
  ['title', 'title'],
  ['greeting', 'greeting'],
  ['otpGreeting', 'otp-greeting'],
  ['preview', 'preview'],
  ['portalMode', 'portal-mode'],
];

/**
 * Shared React wrapper for `<mcp-chat-widget>`.
 * Centralizes attribute syncing and event handling so every embedding page
 * (Portal, WidgetConfigurator, etc.) behaves identically.
 */
const WebchatWidget = forwardRef<HTMLElement, WebchatWidgetProps>(
  function WebchatWidget(props, ref) {
    const internalRef = useRef<HTMLElement>(null);

    // Expose the underlying DOM element to the parent
    useImperativeHandle(ref, () => internalRef.current!, []);

    // Sync props → HTML attributes
    useEffect(() => {
      const el = internalRef.current;
      if (!el) return;

      for (const [prop, attr] of ATTR_MAP) {
        const value = props[prop];
        if (value != null && value !== '') {
          el.setAttribute(attr, String(value));
        } else {
          el.removeAttribute(attr);
        }
      }
    }, [
      props.displayMode,
      props.widgetId,
      props.serverUrl,
      props.auth,
      props.language,
      props.primaryColor,
      props.position,
      props.sessionTimeout,
      props.title,
      props.greeting,
      props.otpGreeting,
      props.preview,
      props.portalMode,
    ]);

    // Bind widget custom events → onEvent callback
    useEffect(() => {
      const el = internalRef.current;
      if (!el || !props.onEvent) return;

      const handler = (e: Event) => {
        props.onEvent!(e.type, (e as CustomEvent).detail ?? null);
      };
      WIDGET_EVENTS.forEach(evt => el.addEventListener(evt, handler));
      return () => WIDGET_EVENTS.forEach(evt => el.removeEventListener(evt, handler));
    }, [props.onEvent]);

    return (
      // @ts-expect-error - custom web component
      <mcp-chat-widget
        ref={internalRef}
        style={props.style}
      />
    );
  },
);

export default WebchatWidget;
